<template>
  <div class="awards-table">
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <div v-if="programs.length > 0">
        <b-table
          :items="groupedRows"
          :fields="tableFields"
          :tbody-tr-class="getRowClass"
          hover
          responsive
          borderless
          show-empty
          class="awards-table__table"
          @row-clicked="onRowClicked"
        >
          <template #cell(select)="{ item }">
            <div class="awards-table__cell awards-table__cell--select">
              <font-awesome-icon
                v-if="item.__group"
                :icon="['fal', expandedGroups.has(item.recognition_award_group_id) ? 'chevron-down' : 'chevron-right']"
                class="awards-table__group-chevron"
                role="button"
                @click="toggleGroup(item.recognition_award_group_id)"
              />
              <template v-else>
                <font-awesome-icon
                  v-if="selectedId === item.recognition_award_program_id"
                  :icon="['fas', 'circle-check']"
                  class="awards-table__select-icon"
                />
                <span v-else class="awards-table__select-empty" />
              </template>
            </div>
          </template>

          <template #cell(group_chevron)="{ item }">
            <div class="awards-table__cell awards-table__cell--select">
              <font-awesome-icon
                v-if="item.__group"
                :icon="['fal', expandedGroups.has(item.recognition_award_group_id) ? 'chevron-down' : 'chevron-right']"
                class="awards-table__group-chevron"
                role="button"
                @click="toggleGroup(item.recognition_award_group_id)"
              />
            </div>
          </template>

          <template #cell(is_active)="{ item }">
            <div class="awards-table__cell">
              <b-badge v-if="!item.__group && viewOnly" :variant="item.is_active ? 'success' : 'secondary'">
                {{ item.is_active ? 'Active' : 'Disabled' }}
              </b-badge>
              <b-form-checkbox
                v-else-if="!item.__group"
                :checked="item.is_active"
                switch
                size="lg"
                class="awards-table__toggle"
                :disabled="togglingProgramId === item.recognition_award_program_id"
                @click.native.prevent="onToggleActive(item)"
              />
            </div>
          </template>

          <template #cell(program_name)="{ item }">
            <div
              v-if="item.__group"
              class="awards-table__cell awards-table__cell--name"
              role="button"
              @click="toggleGroup(item.recognition_award_group_id)"
            >
              <div class="awards-table__badge-wrapper">
                <div class="awards-table__badge-icon">
                  <font-awesome-icon
                    :icon="['fad', item.__other ? 'medal' : getProgramTypeIcon(item.program_type)]"
                    class="awards-table__badge-placeholder"
                  />
                </div>
              </div>
              <span class="awards-table__name">{{ item.recognition_award_group_name }}</span>
            </div>
            <div v-else class="awards-table__cell awards-table__cell--name">
              <div class="awards-table__badge-wrapper">
                <div class="awards-table__badge-icon">
                  <img
                    v-if="item.badge_presigned_url"
                    :src="item.badge_presigned_url"
                    :alt="item.program_name"
                    class="awards-table__badge-image"
                  />
                  <font-awesome-icon
                    v-else
                    :icon="['fad', getProgramTypeIcon(item.program_type)]"
                    class="awards-table__badge-placeholder"
                  />
                </div>
              </div>
              <div class="awards-table__name-group">
                <span v-b-tooltip.hover="item.program_name" class="awards-table__name">
                  {{ item.program_name }}
                </span>
                <span class="awards-table__name-subtitle">
                  {{ getProgramTypeLabel(item.program_type) }}
                </span>
                <span
                  v-if="item.properties && item.properties.short_description"
                  class="awards-table__name-description"
                >
                  {{ item.properties.short_description }}
                </span>
              </div>
            </div>
          </template>

          <template #cell(scope)="{ item }">
            <div v-if="!item.__group" class="awards-table__cell awards-table__cell--scope">
              <template v-if="!item.enabled_divisions || item.enabled_divisions.length === 0">
                <b-badge pill variant="light" class="awards-table__scope-badge">All divisions</b-badge>
              </template>
              <template v-else-if="item.division_names && item.division_names.length === 1">
                <b-badge
                  v-b-tooltip.hover="item.division_names[0]"
                  pill
                  variant="light"
                  class="awards-table__scope-badge"
                >
                  {{ item.division_names[0] }}
                </b-badge>
              </template>
              <template v-else>
                <span :id="`scope-popover-${item.recognition_award_program_id}`" class="awards-table__scope-trigger">
                  <b-badge pill variant="light" class="awards-table__scope-badge">
                    {{ item.enabled_divisions.length }}
                    {{ item.enabled_divisions.length === 1 ? 'division' : 'divisions' }}
                  </b-badge>
                </span>
                <b-popover
                  v-if="item.division_names && item.division_names.length > 1"
                  :target="`scope-popover-${item.recognition_award_program_id}`"
                  triggers="hover focus"
                  placement="bottom"
                >
                  <div class="awards-table__popover-container">
                    <div v-for="name in item.division_names" :key="name">
                      {{ name }}
                    </div>
                  </div>
                </b-popover>
              </template>

              <b-badge v-if="item.enabled_pay_type" pill variant="light" class="awards-table__scope-badge">
                {{ payTypeLabel(item.enabled_pay_type) }}
              </b-badge>
            </div>
          </template>

          <template #cell(nominators)="{ item }">
            <div v-if="!item.__group" class="awards-table__cell">
              <template v-if="item.nominator_names && item.nominator_names.length === 1">
                <b-badge
                  v-b-tooltip.hover="item.nominator_names[0]"
                  pill
                  variant="light"
                  class="awards-table__count-badge"
                >
                  {{ item.nominator_names[0] }}
                </b-badge>
              </template>
              <template v-else-if="item.nominator_names && item.nominator_names.length > 1">
                <span
                  :id="`nominators-popover-${item.recognition_award_program_id}`"
                  class="awards-table__scope-trigger"
                >
                  <b-badge pill variant="light" class="awards-table__count-badge">
                    {{ item.nominator_names.length }} nominators
                  </b-badge>
                </span>
                <b-popover
                  :target="`nominators-popover-${item.recognition_award_program_id}`"
                  triggers="hover focus"
                  placement="bottom"
                >
                  <div class="awards-table__popover-container">
                    <div v-for="name in item.nominator_names" :key="name">
                      {{ name }}
                    </div>
                  </div>
                </b-popover>
              </template>
              <template v-else>
                <span class="text-muted">-</span>
              </template>
            </div>
          </template>

          <template #cell(approvers)="{ item }">
            <div v-if="!item.__group" class="awards-table__cell">
              <template v-if="item.approver_names && item.approver_names.length === 1">
                <b-badge
                  v-b-tooltip.hover="item.approver_names[0]"
                  pill
                  variant="light"
                  class="awards-table__count-badge"
                >
                  {{ item.approver_names[0] }}
                </b-badge>
              </template>
              <template v-else-if="item.approver_names && item.approver_names.length > 1">
                <span
                  :id="`approvers-popover-${item.recognition_award_program_id}`"
                  class="awards-table__scope-trigger"
                >
                  <b-badge pill variant="light" class="awards-table__count-badge">
                    {{ item.approver_names.length }} approvers
                  </b-badge>
                </span>
                <b-popover
                  :target="`approvers-popover-${item.recognition_award_program_id}`"
                  triggers="hover focus"
                  placement="bottom"
                >
                  <div class="awards-table__popover-container">
                    <div v-for="name in item.approver_names" :key="name">
                      {{ name }}
                    </div>
                  </div>
                </b-popover>
              </template>
              <template v-else>
                <span class="text-muted">-</span>
              </template>
            </div>
          </template>

          <template #cell(budget)="{ item }">
            <div v-if="!item.__group" class="awards-table__cell awards-table__cell--budget">
              <template v-if="item.budget_minor">
                <div class="awards-table__budget-text">
                  {{ formatBudget(item.budget_spent_minor ?? 0) }} / {{ formatBudget(item.budget_minor) }}
                </div>
                <div class="text-muted awards-table__budget-remaining">
                  {{ formatBudget(budgetRemainingMinor(item)) }} left
                </div>
                <b-progress
                  :value="item.budget_spent_minor || 0"
                  :max="item.budget_minor"
                  height="4px"
                  class="awards-table__budget-progress"
                />
              </template>
              <template v-else>
                <span class="text-muted">-</span>
              </template>
            </div>
          </template>

          <template #cell(budget_period)="{ item }">
            <div v-if="!item.__group" class="awards-table__cell">
              <template v-if="item.budgetPeriod">
                <span class="text-muted awards-table__budget-period"
                  >({{ formatBudgetPeriod(item.budgetPeriod) }})</span
                >
              </template>
              <template v-else>
                <span class="text-muted"></span>
              </template>
            </div>
          </template>

          <template #cell(pending)="{ item }">
            <div v-if="!item.__group" class="awards-table__cell">
              <template v-if="item.pendingCount > 0">
                <span class="awards-table__pending-count">{{ item.pendingCount }}</span>
              </template>
              <template v-else>
                <span class="text-muted">-</span>
              </template>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="awards-table__cell awards-table__cell--actions">
              <b-button
                v-if="item.__group && !item.__other && !viewOnly"
                size="sm"
                variant="link"
                class="awards-table__group-edit"
                title="Edit group"
                @click="onEditGroup(item)"
              >
                <font-awesome-icon :icon="['fal', 'pen-to-square']" />
              </b-button>
              <router-link
                v-if="!item.__group"
                :to="{ name: 'recognition-award-detail', params: { programId: item.recognition_award_program_id } }"
                class="awards-table__action-arrow"
              >
                <font-awesome-icon :icon="['fal', 'chevron-right']" />
              </router-link>
            </div>
          </template>
        </b-table>
      </div>

      <div v-else class="awards-table__empty">
        <p class="text-muted">No awards found.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';
import AppLoading from '@/components/AppLoading.vue';
import { toCurrency } from '@/utils';
import State from '@/state/State';
import { RECOGNITION_PAY_TYPE_OPTIONS } from '@/pages/recognition/constants';

const payTypeLabel = value => RECOGNITION_PAY_TYPE_OPTIONS.find(o => o.value === value)?.text || value;

const $router = useRouter();

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pendingNominations: {
    type: Array,
    default: () => [],
  },
  togglingProgramId: {
    type: String,
    default: null,
  },
  showDisabledAwards: {
    type: Boolean,
    default: true,
  },
  // Picker mode: render a selection radio instead of the management columns
  // (Active / Pending / Actions) and emit `select` when an award row is clicked.
  selectable: {
    type: Boolean,
    default: false,
  },
  selectedId: {
    type: String,
    default: null,
  },
  // Read-only preview: descriptive columns only, no Active toggle / Pending / row
  // actions (chevron, group pencil) and no group collapsing. Used by the create-review step.
  readonly: {
    type: Boolean,
    default: false,
  },
  // Recognition viewers: the full management list minus everything that writes.
  // Unlike `readonly` the actions column stays, because its chevron is the only
  // route to the award detail page.
  viewOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle-active', 'select']);

const AWARDS_TABLE_FIELDS = [
  {
    key: 'is_active',
    label: 'Active',
    thStyle: { width: '80px' },
  },
  {
    key: 'program_name',
    label: 'Name',
    thStyle: { width: '20%' },
  },
  {
    key: 'scope',
    label: 'Scope',
    thStyle: { width: '12%' },
  },
  {
    key: 'nominators',
    label: 'Nominators',
    thStyle: { width: '13%' },
  },
  {
    key: 'approvers',
    label: 'Approvers',
    thStyle: { width: '13%' },
  },
  {
    key: 'budget',
    label: 'Budget',
    thStyle: { width: '10%' },
  },
  {
    key: 'budget_period',
    label: '',
    thStyle: { width: '100px' },
  },
  {
    key: 'pending',
    label: 'Pending',
    thStyle: { width: '90px' },
  },
  {
    key: 'actions',
    label: '',
    thStyle: { width: '50px' },
  },
];

const SELECT_FIELD = { key: 'select', label: '', thStyle: { width: '48px' } };

// Leftmost column holding the group expand/collapse chevron (empty for normal rows).
const GROUP_CHEVRON_FIELD = { key: 'group_chevron', label: '', thStyle: { width: '32px' } };

// In picker mode keep only the descriptive columns (matching the awards tab) plus
// a leading radio; drop Active / Pending / Actions which are management-only.
const PICKER_FIELD_KEYS = ['program_name', 'scope', 'nominators', 'approvers', 'budget', 'budget_period'];

const tableFields = computed(() => {
  if (props.selectable) {
    return [SELECT_FIELD, ...AWARDS_TABLE_FIELDS.filter(field => PICKER_FIELD_KEYS.includes(field.key))];
  }
  if (props.readonly) {
    // Group chevron + Active + descriptive columns; no Pending / row actions.
    const keys = ['is_active', ...PICKER_FIELD_KEYS];
    return [GROUP_CHEVRON_FIELD, ...AWARDS_TABLE_FIELDS.filter(field => keys.includes(field.key))];
  }
  if (props.viewOnly) {
    // Everything except Pending, which is derived from nominations a viewer cannot read.
    return [GROUP_CHEVRON_FIELD, ...AWARDS_TABLE_FIELDS.filter(field => field.key !== 'pending')];
  }
  return [GROUP_CHEVRON_FIELD, ...AWARDS_TABLE_FIELDS];
});

const currency = computed(() => State.state?.company?.default_currency);

// Count pending nominations per program
const pendingCountByProgramId = computed(() => {
  const map = {};
  for (const nomination of props.pendingNominations) {
    const id = nomination.recognition_award_program_id;
    if (id) {
      map[id] = (map[id] || 0) + 1;
    }
  }
  return map;
});

// Enrich programs with computed properties from existing data
const enrichedPrograms = computed(() => {
  return props.programs.map(program => {
    const properties = program.properties || {};
    const budgetPeriod = properties.budget_period;

    return {
      ...program,
      budgetPeriod,
      pendingCount: pendingCountByProgramId.value[program.recognition_award_program_id] || 0,
    };
  });
});

const filteredPrograms = computed(() => {
  // The "Show disabled awards" toggle only exists in the management list. Readonly
  // (review preview) and picker modes always show every row so the displayed rows
  // match what's actually submitted/duplicated — e.g. an award toggled inactive
  // during review must stay visible, not vanish while still being created.
  if (props.readonly || props.selectable || props.showDisabledAwards) {
    return enrichedPrograms.value;
  }
  return enrichedPrograms.value.filter(p => p.is_active !== false);
});

// Synthetic group id for the "Other" bucket of awards that aren't in a real group.
const OTHER_GROUP_ID = '__other__';

// Group ids the user has expanded to reveal their member awards. Persisted so the
// list looks the same after navigating into an award and back.
const EXPANDED_GROUPS_STORAGE_KEY = 'sme.recognition.expandedAwardGroups';

function loadExpandedGroups() {
  try {
    const ids = JSON.parse(localStorage.getItem(EXPANDED_GROUPS_STORAGE_KEY) || '[]');
    return new Set(Array.isArray(ids) ? ids : []);
  } catch (e) {
    return new Set();
  }
}

function persistExpandedGroups(set) {
  try {
    localStorage.setItem(EXPANDED_GROUPS_STORAGE_KEY, JSON.stringify([...set]));
  } catch (e) {
    // Storage unavailable (private mode/quota) — expansion just won't persist.
  }
}

// Read-only previews auto-expand everything below, so don't seed from storage there.
const expandedGroups = ref(props.readonly ? new Set() : loadExpandedGroups());

// In read-only previews, expand every group by default so the awards are visible.
watch(
  filteredPrograms,
  programs => {
    if (!props.readonly) {
      return;
    }
    const ids = new Set();
    for (const program of programs) {
      if (program.recognition_award_group_id) {
        ids.add(program.recognition_award_group_id);
      }
    }
    expandedGroups.value = ids;
  },
  { immediate: true },
);

const toggleGroup = groupId => {
  const next = new Set(expandedGroups.value);
  if (next.has(groupId)) {
    next.delete(groupId);
  } else {
    next.add(groupId);
  }
  expandedGroups.value = next;
  if (!props.readonly) {
    persistExpandedGroups(next);
  }
};

// Collapse awards that share a group into a single synthetic group row. When a
// group is expanded, its member awards are spliced in directly beneath it so they
// render as ordinary award rows.
const groupedRows = computed(() => {
  const groups = new Map();
  const ordered = [];
  const ungrouped = [];
  for (const program of filteredPrograms.value) {
    const groupId = program.recognition_award_group_id;
    if (!groupId) {
      ungrouped.push(program);
      continue;
    }
    if (!groups.has(groupId)) {
      const groupRow = {
        __group: true,
        recognition_award_group_id: groupId,
        recognition_award_group_name: program.recognition_award_group_name,
        // Groups are duplicated from a single source, so members share a type.
        program_type: program.program_type,
        count: 0,
        members: [],
      };
      groups.set(groupId, groupRow);
      ordered.push(groupRow);
    }
    const groupRow = groups.get(groupId);
    groupRow.count += 1;
    groupRow.members.push(program);
  }

  const pushGroup = (groupRow, rows) => {
    rows.push(groupRow);
    if (expandedGroups.value.has(groupRow.recognition_award_group_id)) {
      for (const member of groupRow.members) {
        rows.push({ ...member, __groupMember: true });
      }
    }
  };

  const rows = [];
  for (const groupRow of ordered) {
    pushGroup(groupRow, rows);
  }
  // All awards that aren't in a real group are collected under a synthetic "Other" group.
  if (ungrouped.length) {
    pushGroup(
      {
        __group: true,
        __other: true,
        recognition_award_group_id: OTHER_GROUP_ID,
        recognition_award_group_name: 'Awards',
        count: ungrouped.length,
        members: ungrouped,
      },
      rows,
    );
  }
  return rows;
});

const getRowClass = item => {
  if (item.__group) {
    return 'awards-table__row--group';
  }
  const classes = [];
  if (item.__groupMember) {
    classes.push('awards-table__row--member');
  }
  if (!item.is_active) {
    classes.push('awards-table__row--disabled');
  }
  if (props.selectable) {
    classes.push('awards-table__row--selectable');
    if (item.recognition_award_program_id === props.selectedId) {
      classes.push('awards-table__row--selected');
    }
  }
  return classes.join(' ');
};

// Picker mode only: clicking an award row selects it as the duplication source.
// Group header rows keep their own expand/collapse handler.
const onRowClicked = item => {
  if (!props.selectable || item.__group) {
    return;
  }
  emit('select', item);
};

const onEditGroup = item => {
  $router.push({ name: 'recognition-group-editor', query: { groupId: item.recognition_award_group_id } });
};

const onToggleActive = item => {
  emit('toggle-active', {
    programId: item.recognition_award_program_id,
    programName: item.program_name,
    currentIsActive: item.is_active,
  });
};

const formatBudget = value => {
  if (value == null) {
    return '-';
  }
  return toCurrency(value, currency.value).replace(/\.00$/, '');
};

// Remaining budget: prefer the backend-computed value, falling back to budget - spent.
const budgetRemainingMinor = item => {
  if (item.budget_remaining_minor != null) {
    return item.budget_remaining_minor;
  }
  return (item.budget_minor ?? 0) - (item.budget_spent_minor ?? 0);
};

const formatBudgetPeriod = period => {
  const periodMap = {
    week: 'weekly',
    month: 'monthly',
    quarter: 'quarterly',
    year: 'yearly',
  };
  return periodMap[period?.toLowerCase()] || period;
};

const getProgramTypeIcon = programType => {
  const iconMap = {
    PROGRAM: 'gift',
    OPEN: 'party-horn',
    MILESTONE: 'award',
  };
  return iconMap[programType] || 'award';
};

const getProgramTypeLabel = programType => {
  const labelMap = {
    PROGRAM: 'Program Award',
    OPEN: 'Open Award',
    MILESTONE: 'Milestone Award',
  };
  return labelMap[programType] || programType;
};
</script>

<style scoped lang="scss">
.awards-table {
  // Fixed layout so column widths come from the header (the thStyle widths in JS),
  // not from each row's content. Without this, expanding/collapsing a group swaps
  // in member rows whose content re-computes every column's width, shuffling the
  // table sideways. Target the real <table> (.b-table) via :deep — with the
  // `responsive` wrapper the class/scoped attr can land on the wrapping div, not
  // the table itself.
  :deep(table.b-table) {
    table-layout: fixed;
    width: 100%;
  }

  &__table {
    // Drop Bootstrap's default 1rem table margin so there's no gap under the last row.
    margin-bottom: 0;

    :deep(.awards-table__row--disabled) {
      opacity: 0.5;
    }
    // Group header row: white like the rest of the table.
    :deep(.awards-table__row--group) {
      background-color: var(--palette-color-base-white);
      cursor: pointer;
    }
    :deep(.awards-table__row--group:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
    :deep(.awards-table__row--group td) {
      padding-top: 0.4rem;
      padding-bottom: 0.4rem;
    }
    // Member award rows nested under an expanded group: subtle indent.
    :deep(.awards-table__row--member td:first-child) {
      box-shadow: inset 3px 0 0 var(--palette-color-default-lighten-85);
    }
    // Picker mode: rows are clickable and the selected one is highlighted.
    :deep(.awards-table__row--selectable) {
      cursor: pointer;
    }
    :deep(.awards-table__row--selected) {
      background-color: var(--palette-color-core-1-lighten-80);
    }
    :deep(.awards-table__row--selected:hover) {
      background-color: var(--palette-color-core-1-lighten-70);
    }
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem 0.75rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__cell {
    display: flex;
    align-items: center;

    &--scope {
      gap: 0.375rem;
      flex-wrap: wrap;

      // Without this the badges shrink to share the column rather than wrapping,
      // which truncates labels that would otherwise fit.
      .awards-table__scope-badge,
      .awards-table__scope-trigger {
        flex-shrink: 0;
      }
    }

    &--group {
      gap: 0.5rem;
      font-weight: 600;
      font-size: 0.875rem;
    }

    &--name {
      gap: 0.75rem;
    }

    &--budget {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }

    &--actions {
      justify-content: flex-end;
    }

    &--select {
      justify-content: center;
    }
  }

  &__select-icon {
    color: var(--palette-color-brand-primary);
    font-size: 1rem;
  }

  &__select-empty {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border: 2px solid var(--palette-color-default-lighten-80);
    border-radius: 50%;
  }

  &__group-chevron {
    color: var(--palette-color-default-lighten-40);
    font-size: 0.75rem;
    width: 0.75rem;
  }

  // Matches __action-arrow so the group pencil lines up with the chevron on regular rows.
  &__group-edit {
    color: var(--palette-color-default-lighten-40);
    padding: 0.5rem;
    line-height: 1;
    border: none;

    &:hover {
      color: var(--palette-color-brand-primary);
    }

    // No focus ring or grey background box on hover/click (bootstrap .btn adds them).
    &:hover,
    &:focus,
    &:active {
      outline: none !important;
      box-shadow: none !important;
      background: none !important;
    }
  }

  &__toggle {
    :deep(.custom-control-label::before),
    :deep(.custom-control-label::after) {
      width: 2.5rem;
      height: 1.25rem;
    }

    :deep(.custom-control-input:checked ~ .custom-control-label::before) {
      background-color: var(--palette-color-brand-primary);
      border-color: var(--palette-color-brand-primary);
    }
  }

  &__badge-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background-color: var(--palette-color-core-1-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__badge-image {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }

  &__badge-placeholder {
    color: var(--palette-color-brand-primary);
    font-size: 1.25rem;
  }

  &__badge-wrapper {
    position: relative;
    flex-shrink: 0;
  }

  &__name-group {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  &__name {
    font-size: 0.875rem; // match the budget cell font (&__budget-text)
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 10rem;
  }

  &__name-subtitle {
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--palette-color-default-lighten-30);
    white-space: nowrap;
  }

  &__name-description {
    font-size: 0.6875rem;
    font-weight: 400;
    color: var(--palette-color-default-lighten-40);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 10rem;
  }

  &__scope-badge,
  &__count-badge {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 400;
    padding: 0.375rem 0.75rem;
    cursor: default;
    display: inline-block;
    max-width: 8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  &__scope-trigger {
    cursor: pointer;
  }

  &__popover-container {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__budget-text {
    font-size: 0.875rem;
    font-weight: 500;
  }

  &__budget-period {
    font-size: 0.875rem;
  }

  &__pending-count {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--palette-color-brand-primary);
  }

  &__budget-progress {
    width: 100%;
    min-width: 80px;
    // Group the two amount lines together (tight 0.25rem cell gap) and separate
    // the status bar beneath them.
    margin-top: 0.375rem;
    border-radius: 0.125rem;
    background-color: var(--palette-color-core-1-lighten-60);
  }

  &__action-arrow {
    color: var(--palette-color-default-lighten-40);
    padding: 0.5rem;

    &:hover {
      color: var(--palette-color-brand-primary);
    }
  }

  &__empty {
    padding: 2rem;
    text-align: center;
  }
}
</style>
